import React, { useCallback, useMemo, useState } from 'react';

import { Form } from '@opendoor/atoms/form';
import type { AtomsInputSize } from '@opendoor/atoms/input';
import { cn } from '@opendoor/atoms/utils';
import { parseDirectPhoneNumber } from '@opendoor/shared-ts/phone';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

import { z } from '../../zodMini';
import { MarketingOptIn } from './MarketingOptIn';
import {
  resolveMarketingOptIn,
  type SmsConsentSpec,
  smsConsentValues,
  splitConsentDescription,
} from './smsConsent';

/** One phone field's consent state, from {@link useSmsConsentState}. */
export interface SmsConsentState {
  /** The spec these values were collected for; carried so the two can't be paired wrong. */
  spec: SmsConsentSpec;
  /** Whether a given checkbox is checked. Unknown names read as unchecked. */
  isChecked: (name: string) => boolean;
  setChecked: (name: string, checked: boolean) => void;
  /** What a `marketingSmsOptOut` write should record — the preference-driving box's state. */
  optIn: boolean;
  /** Every box in the spec with its final state, for the submission's own record. */
  values: Readonly<Record<string, boolean>>;
}

/**
 * Holds the checkbox state for one {@link PhoneConsentFields} group.
 *
 * The boxes are deliberately not fields on the surrounding TanStack form: they
 * carry no validation (consent is never required to submit) and their names come
 * from the spec, so keeping them here lets a surface swap specs without
 * rebuilding its schema. Derived values are keyed off the spec on every read, so
 * a spec change can't leave stale names behind.
 */
export function useSmsConsentState(spec: SmsConsentSpec): SmsConsentState {
  const [checked, setCheckedState] = useState<Record<string, boolean>>({});

  const setChecked = useCallback((name: string, next: boolean) => {
    setCheckedState((current) => ({ ...current, [name]: next }));
  }, []);

  return useMemo(
    () => ({
      spec,
      isChecked: (name: string) => checked[name] === true,
      setChecked,
      optIn: resolveMarketingOptIn(spec, checked),
      values: smsConsentValues(spec, checked),
    }),
    [spec, checked, setChecked],
  );
}

/**
 * The TanStack field name this binds. Fixed rather than a prop: the field and
 * the rule that validates it have to agree, and a host free to rename one but
 * not the other gets a phone input with no validation and no error slot.
 */
export const PHONE_FIELD_NAME = 'phoneNumber';

const INVALID_PHONE_MESSAGE = 'Enter a valid phone number';

/**
 * The rule for {@link PHONE_FIELD_NAME}, which a host merges into its own
 * schema — `z.object({ ...yourFields, [PHONE_FIELD_NAME]: phoneNumberSchema })`.
 *
 * Required, and required is the only shape offered: this module pairs a number
 * with the consent naming it, and consent for a number nobody supplied is not
 * a thing worth recording. A surface that wants an optional phone field wants a
 * phone input, not this — compose atoms' `Form.InputPhone` directly.
 */
export const phoneNumberSchema = z.string().check(
  z.trim(),
  z.refine((value) => parseDirectPhoneNumber(value) !== undefined, INVALID_PHONE_MESSAGE),
);

/**
 * The typed value as E.164, falling back to the input when it will not parse.
 *
 * Ships beside the field and its rule for the same reason they do: a host that
 * collects the number is the one that writes it, and every write-path should see
 * the same shape. {@link phoneNumberSchema} has already guaranteed the value
 * parses by the time a submit handler runs, so the fallback is for callers that
 * normalize outside that guarantee.
 */
export function normalizePhoneNumber(value: string): string {
  return parsePhoneNumberFromString(value, 'US')?.format('E.164') ?? value;
}

export interface PhoneConsentFieldsProps {
  /** Consent state from {@link useSmsConsentState}, which carries its own spec. */
  consent: SmsConsentState;
  /**
   * `APP-COMPONENT` of the owning surface. The phone field appends `-phone` and
   * each checkbox appends its spec `analyticsSuffix`.
   */
  analyticsPrefix: string;
  /** Phone field label. @default "Phone number" */
  label?: string;
  /** Atoms input size for the phone field. @default "base" */
  inputSize?: AtomsInputSize;
  className?: string;
}

const DEFAULT_LABEL = 'Phone number';

/**
 * A phone number and the SMS consent that names it, as one unit.
 *
 * The consent copy says "to the number provided", so it belongs to the field
 * that collects the number rather than to the screen around it — every surface
 * that asks for a phone number renders this, and gets the same disclosures in
 * the same place. How many boxes and what they say is the spec's, so a surface
 * that records consent under its own version supplies its own.
 *
 * Renders inside an atoms `Form.Root`: the phone field binds
 * {@link PHONE_FIELD_NAME} and is error-rendered by the host's schema, which
 * must validate that key with {@link phoneNumberSchema}. The checkboxes are
 * controlled through `consent` and carry no validation — consent is never
 * required to submit.
 */
export const PhoneConsentFields: React.FC<PhoneConsentFieldsProps> = ({
  consent,
  analyticsPrefix,
  label = DEFAULT_LABEL,
  inputSize,
  className,
}) => {
  return (
    <div className={cn('flex w-full flex-col gap-4', className)}>
      <Form.InputPhone
        name={PHONE_FIELD_NAME}
        label={label}
        autoComplete="tel"
        analyticsName={`${analyticsPrefix}-phone`}
        size={inputSize}
      />
      {consent.spec.checkboxes.map((box) => {
        const { summary, detail } = splitConsentDescription(box);
        return (
          <MarketingOptIn
            key={box.name}
            checked={consent.isChecked(box.name)}
            onCheckedChange={(next) => consent.setChecked(box.name, next)}
            label={box.label}
            description={summary}
            detail={detail}
            analyticsName={`${analyticsPrefix}-${box.analyticsSuffix}`}
            // The spec's own key, so the click event's `optionName` matches the
            // name this box is recorded under in `values`.
            value={box.name}
          />
        );
      })}
    </div>
  );
};
